import '../se-heading/se-heading';
import '../se-social-link/se-social-link';
import '../se-button/se-button';
import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-profile-hero.inner.pcss?inline';
import { SlotControllerLegacy } from '../../controllers/slot-controller-legacy';

/**
 * The Profile Hero component.
 * @element se-profile-hero
 */
@customElement('se-profile-hero')
export class SeProfileHero extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });
  slots = new SlotControllerLegacy(this, true);

  /**
   * Profile Hero required attributes
   */
  @property({ type: String, attribute: 'full-name' })
  fullName: string;
  @property({ type: String, attribute: 'main-title' })
  mainTitle: string;
  @property({ type: String, attribute: 'v-card' })
  vCard: string;
  @property({ type: String, attribute: 'primary-office-location' })
  primaryOfficeLocation: string;
  @property({ type: String, attribute: 'primary-office-url' })
  primaryOfficeURL: string;
  // Programatically set url target for the print button functionality.
  @property({ type: String })
  printableProfileUrl: string;
  // Used as the base URL for the selectable print options.
  @property({ type: String, attribute: 'print-url-slug' })
  printUrlSlug: string;
  @property({ type: Boolean })
  printOpen = false;

  /**
   * Profile Hero optional attributes
   */
  @property({ type: String, attribute: 'pronunciation' })
  pronunciation: string | boolean = false;
  @property({ type: String, attribute: 'gender-pronouns' })
  genderPronouns: string | boolean = false;
  @property({ type: String, attribute: 'position' })
  position: string | boolean = false;
  @property({ type: String, attribute: 'email' })
  email: string | boolean = false;
  @property({ type: String, attribute: 'phone' })
  phone: string | boolean = false;
  @property({ type: String, attribute: 'phone2' })
  phone2: string | boolean = false;
  @property({ type: String, attribute: 'fax' })
  fax: string | boolean = false;
  @property({ type: String, attribute: 'linkedin' })
  linkedin: string | boolean = false;
  @property({ type: String, attribute: 'twitter' })
  twitter: string | boolean = false;
  @property({ type: String, attribute: 'facebook' })
  facebook: string | boolean = false;
  @property({ type: String, attribute: 'secondary-office-location' })
  secondaryOfficeLocation: string | boolean = false;
  @property({ type: String, attribute: 'secondary-office-url' })
  secondaryOfficeURL: string | boolean = false;

  /**
   * Props for print exclusion options.
   */
  @property({ type: Boolean })
  experience: boolean;
  @property({ type: Boolean })
  outside: boolean;

  checked: string[] = [];

  firstUpdated() {
    this.printableProfileUrl = this.printUrlSlug + '/print';
  }

  bgTemplate(): TemplateResult {
    return html`
      <div class="background-image">
        <slot name="background-image"></slot>
      </div>
    `;
  }

  cardTemplate(): TemplateResult {
    return html`
      <section class="profile-card">
        <se-container>
          <div class="card-wrapper">
            <se-heading level="h1" class="full-name"
              >${this.fullName}</se-heading
            >
            ${this.pronunciation || this.genderPronouns
              ? html`
                  <span class="prons">
                    ${this.pronunciation
                      ? html`<em>${this.pronunciation}</em>`
                      : ''}
                    ${this.pronunciation && this.genderPronouns
                      ? html`<span class="divider"></span>`
                      : ''}
                    ${this.genderPronouns ? html`${this.genderPronouns}` : ''}
                  </span>
                `
              : ''}
            <p class="main-title">${this.mainTitle}</p>

            ${this.position
              ? html` <p class="secondary-position">${this.position}</p> `
              : ''}
            ${this.email
              ? html`
                  <a class="email" href="mailto:${this.email}">${this.email}</a>
                `
              : ''}
            ${this.phone
              ? html`
                  <a class="phone" href="tel:${this.phone}">${this.phone}</a>
                `
              : ''}
            ${this.phone2
              ? html`
                  <a class="phone" href="tel:${this.phone2}">${this.phone2}</a>
                `
              : ''}
            ${this.fax ? html` <div class="fax">Fax: ${this.fax}</div> ` : ''}
            ${this.linkedin || this.twitter || this.facebook
              ? html`
                  <nav aria-label="Social links">
                    <ul class="social">
                      ${this.linkedin
                        ? html`
                            <li>
                              <se-social-link
                                variant="linkedin"
                                url="${this.linkedin}"
                              ></se-social-link>
                            </li>
                          `
                        : ''}
                      ${this.twitter
                        ? html`
                            <li>
                              <se-social-link
                                variant="twitter"
                                url="${this.twitter}"
                              ></se-social-link>
                            </li>
                          `
                        : ''}
                      ${this.facebook
                        ? html`
                            <li>
                              <se-social-link
                                variant="facebook"
                                url="${this.facebook}"
                              ></se-social-link>
                            </li>
                          `
                        : ''}
                    </ul>
                  </nav>
                `
              : ''}

            <se-button
              class="download-vcard"
              button-url="${this.vCard}"
              variant="secondary"
              downloadable
            >
              <outline-icon
                slot="left"
                name="calendar"
                library="system"
                size="1em"
                color="green-400"
              ></outline-icon>
              Download vCard
            </se-button>
          </div>
        </se-container>
      </section>
    `;
  }

  extraTemplate(): TemplateResult {
    return html`
      <section class="extra">
        <se-container>
          <div class="right-side-wrapper">
            <div class="card-wrapper">
              <nav aria-label="Locations information" class="offices-info">
                <outline-icon
                  name="map-marker"
                  library="system"
                  size="1.5rem"
                ></outline-icon>
                <ul>
                  <li class="office-label">
                    <slot name="primary-office"></slot>
                  </li>
                  <li class="office-link">
                    <a href="${this.primaryOfficeURL}">
                      ${this.primaryOfficeLocation}
                    </a>
                  </li>
                </ul>
                ${this.secondaryOfficeLocation && this.secondaryOfficeURL
                  ? html`
                      <ul>
                        <li class="office-label">Secondary Office</li>
                        <li class="office-link">
                          <a href="${this.secondaryOfficeURL}">
                            ${this.secondaryOfficeLocation}
                          </a>
                        </li>
                      </ul>
                    `
                  : ''}
              </nav>
              <div class="profile__print-button">
                <se-button
                  class="printable-profile-url"
                  variant="primary-2"
                  @click=${this.togglePrintMenu}
                  @keyup=${this.togglePrintMenu}
                  tabindex="0"
                >
                  <outline-icon
                    slot="left"
                    name=${!this.printOpen ? 'chevron-down' : 'chevron-up'}
                    library="system"
                    size=".75em"
                  ></outline-icon>
                  Print Profile
                </se-button>
                <div
                  class="profile__print-form ${this.printOpen
                    ? 'open'
                    : 'closed'}"
                >
                  <ul aria-label="print menu">
                    ${this.experience || this.outside
                      ? html`
                          <li>
                            <p>Sections to Exclude</p>
                          </li>
                        `
                      : null}
                    ${this.experience
                      ? html`
                      <li>
                        <input type="checkbox" @change="${this.updatePrintURL}" name="print-select" id="experience" value="experience"></input>
                        <label for="insight">Experience</label>
                      </li>
                      `
                      : null}
                    ${this.outside
                      ? html`
                      <li>
                        <input type="checkbox" @change="${this.updatePrintURL}" name="print-select" id="outside" value="outside"></input>
                        <label for="overview">Outside the Firm</label>
                      </li>
                      `
                      : null}
                    <li>
                      <a
                        class="profile__download-link"
                        id="print-form-button"
                        target="_blank"
                        href="${this.printableProfileUrl}"
                        >Print Now
                        <outline-icon
                          color="brand-secondary-1"
                          library="system"
                          name="download"
                          size="1em"
                        >
                        </outline-icon>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </se-container>
      </section>
    `;
  }

  updatePrintURL(e: Event) {
    const printURL = this.printUrlSlug + '/print';
    let params: string;
    const target = e.target as HTMLInputElement;

    if (target.checked) {
      this.checked.push(target.value);
    }

    if (!target.checked) {
      this.checked = this.checked.filter(el => el !== target.value);
    }

    params = '';
    this.checked.forEach(
      (el, i) => (params += i > 0 ? `_${el}` : `?restrict_sections=${el}`)
    );
    this.printableProfileUrl = printURL + params;
  }

  togglePrintMenu(e: Event) {
    if (e.type === 'keyup') {
      const event = e as KeyboardEvent;
      if (event.key === 'Enter' || event.key === ' ') {
        this.printOpen = !this.printOpen;
      }
    } else if (e.type === 'click') {
      this.printOpen = !this.printOpen;
    }
  }

  render(): TemplateResult {
    return html`
      <section class="profile-hero">
        ${this.bgTemplate()} ${this.cardTemplate()} ${this.extraTemplate()}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-profile-hero': SeProfileHero;
  }
}
